import React, { useMemo, useState } from "react";
import { AnimatePresence, motion, useMotionValue, useTransform } from "framer-motion";
import type { ArtistDTO } from "../lib/api";
import type { PreferenceMap, Rating } from "../lib/preferences";
import { IconX, IconMeh, IconHeart, IconStar, IconUndo } from "../components/icons";

const SWIPE_THRESHOLD = 110;

export function Swipe({
  artists,
  prefs,
  onRate,
}: {
  artists: ArtistDTO[];
  prefs: PreferenceMap;
  onRate: (slug: string, rating: Rating | null) => void;
}) {
  // Snapshot of the un-rated deck at mount time. We only re-compute when the
  // artist list itself changes, otherwise every swipe would reshuffle the deck.
  const initialDeck = useMemo(
    () => artists.filter(a => !prefs[a.slug]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [artists],
  );
  const [index, setIndex] = useState(0);
  const [history, setHistory] = useState<Array<{ slug: string; prev: Rating | null }>>([]);

  const current = initialDeck[index];
  const next = initialDeck[index + 1];

  const rate = (rating: Rating) => {
    if (!current) return;
    const prev = prefs[current.slug] ?? null;
    setHistory(h => [...h, { slug: current.slug, prev }]);
    setIndex(i => i + 1);
    onRate(current.slug, rating);
  };

  const undo = () => {
    const last = history[history.length - 1];
    if (!last) return;
    setHistory(h => h.slice(0, -1));
    setIndex(i => Math.max(0, i - 1));
    onRate(last.slug, last.prev);
  };

  const total = artists.length;
  const rated = total - initialDeck.length + index;

  return (
    <div data-testid="view-swipe">
      <div className="section-head">
        <div>
          <span className="super">BUILD YOUR LINEUP</span>
          <h2>Swipe deck</h2>
        </div>
        <button
          className="chip"
          onClick={undo}
          disabled={history.length === 0}
          aria-label="Undo last swipe"
          data-testid="undo"
          style={{ opacity: history.length === 0 ? 0.4 : 1 }}
        >
          <IconUndo /> undo
        </button>
      </div>

      <div className="swipe-progress">
        <span>{String(rated).padStart(3, "0")} / {String(total).padStart(3, "0")}</span>
        <div className="bar">
          <div className="fill" style={{ width: `${total ? (rated / total) * 100 : 0}%` }} />
        </div>
        <span>{Math.round(total ? (rated / total) * 100 : 0)}%</span>
      </div>

      <div className="deck-area">
        <div className="swipe-stack">
          {!current && (
            <div className="swipe-card placeholder" data-testid="deck-empty">
              <div>
                <div style={{ fontSize: 40, marginBottom: 14 }}>🎧</div>
                <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>Deck is empty.</div>
                <div style={{ fontSize: 13 }}>
                  You've swiped every artist. Visit <b>Lineup</b> to see your schedule.
                </div>
              </div>
            </div>
          )}

          {next && <DeckCard key={next.slug} artist={next} depth={1} />}

          <AnimatePresence>
            {current && <DraggableCard key={current.slug} artist={current} onSwipe={rate} />}
          </AnimatePresence>
        </div>
      </div>

      <div className="swipe-actions" role="group" aria-label="Swipe actions">
        <button className="sa-btn pass" onClick={() => rate("pass")} aria-label="Pass" data-testid="btn-pass" disabled={!current}>
          <IconX />
        </button>
        <button className="sa-btn meh" onClick={() => rate("meh")} aria-label="Meh" data-testid="btn-meh" disabled={!current}>
          <IconMeh />
        </button>
        <button className="sa-btn like" onClick={() => rate("like")} aria-label="Like" data-testid="btn-like" disabled={!current}>
          <IconHeart />
        </button>
        <button className="sa-btn superlike" onClick={() => rate("superlike")} aria-label="Superlike" data-testid="btn-superlike" disabled={!current}>
          <IconStar />
        </button>
      </div>
    </div>
  );
}

function DeckCard({ artist, depth }: { artist: ArtistDTO; depth: number }) {
  const color = artist.sets[0]?.stage.color ?? "#ff6a13";
  return (
    <div
      className="swipe-card"
      style={{
        ["--glow" as never]: `${color}60`,
        ["--glow-solid" as never]: color,
        transform: `scale(${1 - depth * 0.04}) translateY(${depth * 10}px)`,
        opacity: 1 - depth * 0.3,
      }}
      aria-hidden="true"
    >
      {artist.photoUrl ? (
        <div className="sc-photo" style={{ backgroundImage: `url(${artist.photoUrl})` }} />
      ) : (
        <div className="sc-photo no-photo" />
      )}
      <div className="sc-veil" />
      <div className="sc-glow" />
      <div className="sc-stage">
        <span><span className="dot" />{artist.sets[0]?.stage.name ?? "STAGE"}</span>
        <span>D{artist.sets[0]?.day ?? "-"}</span>
      </div>
      <div className="sc-body">
        <div className="sc-name">{artist.name}</div>
        <div className="sc-genre">{artist.genre}</div>
      </div>
    </div>
  );
}

function DraggableCard({
  artist,
  onSwipe,
}: {
  artist: ArtistDTO;
  onSwipe: (rating: Rating) => void;
}) {
  const color = artist.sets[0]?.stage.color ?? "#ff6a13";
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-18, 18]);
  const likeOp = useTransform(x, [40, 140], [0, 1]);
  const passOp = useTransform(x, [-140, -40], [1, 0]);
  const superOp = useTransform(y, [-140, -40], [1, 0]);
  const mehOp = useTransform(y, [40, 140], [0, 1]);

  return (
    <motion.div
      className="swipe-card"
      data-testid="swipe-card"
      data-slug={artist.slug}
      drag
      dragElastic={0.8}
      dragMomentum={false}
      style={{
        ["--glow" as never]: `${color}60`,
        ["--glow-solid" as never]: color,
        x, y, rotate,
      }}
      onDragEnd={(_, info) => {
        if (info.offset.x > SWIPE_THRESHOLD) return onSwipe("like");
        if (info.offset.x < -SWIPE_THRESHOLD) return onSwipe("pass");
        if (info.offset.y < -SWIPE_THRESHOLD) return onSwipe("superlike");
        if (info.offset.y > SWIPE_THRESHOLD) return onSwipe("meh");
      }}
      initial={{ scale: 0.96, opacity: 0.2 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{
        x: x.get() > 0 ? 600 : x.get() < 0 ? -600 : 0,
        y: y.get() < 0 ? -600 : y.get() > 0 ? 600 : 0,
        opacity: 0,
        transition: { duration: 0.18 },
      }}
    >
      {artist.photoUrl ? (
        <div className="sc-photo" style={{ backgroundImage: `url(${artist.photoUrl})` }} />
      ) : (
        <div className="sc-photo no-photo" />
      )}
      <div className="sc-veil" />
      <div className="sc-glow" />
      <div className="sc-stage">
        <span><span className="dot" />{artist.sets[0]?.stage.name ?? "STAGE"}</span>
        <span>D{artist.sets[0]?.day ?? "-"}</span>
      </div>
      <div className="sc-body">
        <div className="sc-name">{artist.name}</div>
        <div className="sc-genre">{artist.genre}</div>
        {artist.bio && <div className="sc-bio">{artist.bio}</div>}
        <div className="sc-sets">
          {artist.sets.map(s => (
            <div key={s.id} className="sc-set">
              D{s.day}
              {s.startLabel ? ` · ${s.startLabel} → ${s.endLabel}` : ""}
              {" · "}
              {s.stage.name}
            </div>
          ))}
        </div>
      </div>
      <motion.div className="stamp left" style={{ opacity: passOp }}>PASS</motion.div>
      <motion.div className="stamp right" style={{ opacity: likeOp }}>LIKE</motion.div>
      <motion.div className="stamp up" style={{ opacity: superOp }}>SUPER</motion.div>
      <motion.div className="stamp down" style={{ opacity: mehOp }}>MEH</motion.div>
    </motion.div>
  );
}
